import Loader2 from 'lucide-react/dist/esm/icons/loader-2'
import Trash2 from 'lucide-react/dist/esm/icons/trash-2'
import FileText from 'lucide-react/dist/esm/icons/file-text'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'

interface DeleteDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fileName: string
  onConfirm: () => void
  isDeleting?: boolean
}

/** Confirmation dialog shown before removing an uploaded verification document. */
export function DeleteDocumentDialog({
  open,
  onOpenChange,
  fileName,
  onConfirm,
  isDeleting = false,
}: DeleteDocumentDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[420px]">
        <DialogCloseButton className="right-3.5 top-3.5" disabled={isDeleting} />

        <div className="space-y-5 px-6 pb-2 pt-6">
          <div className="flex items-start gap-4 pr-6">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 ring-1 ring-inset ring-red-100"
              aria-hidden="true"
            >
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div className="min-w-0 space-y-1.5 pt-0.5">
              <DialogTitle className="text-base font-semibold leading-snug sm:text-lg">
                Remove this document?
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed">
                This file will be taken out of your verification submission. You can upload a
                replacement whenever you&apos;re ready.
              </DialogDescription>
            </div>
          </div>

          <div className="rounded-xl border border-border/70 bg-muted/25 px-4 py-3.5">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Document to remove
            </p>
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-border/50">
                <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </span>
              <p className="min-w-0 truncate text-sm font-medium text-foreground" title={fileName}>
                {fileName}
              </p>
            </div>
          </div>

          <p className="rounded-lg border border-amber-200/80 bg-amber-50/80 px-3.5 py-3 text-sm leading-relaxed text-amber-950/90">
            This requirement will show as incomplete until you upload a new file.
          </p>
        </div>

        <DialogFooter className="mt-2 gap-2.5 border-t border-border/60 bg-muted/15 px-6 py-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto"
            disabled={isDeleting}
            onClick={() => onOpenChange(false)}
          >
            Keep document
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="w-full gap-2 sm:w-auto"
            disabled={isDeleting}
            onClick={onConfirm}
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Removing…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Remove document
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
